import { setBusy } from './ui.js';

const BOXED_ROW_VIEW = 'rows';
const BOXED_CARD_VIEW = 'cards';
const BOXED_MOBILE_CARD_VIEW_QUERY = '(max-width: 767.98px)';
const boxedViewControllers = new WeakMap();
let boxedViewId = 0;

function addClass(element, className) {
    if (element && !element.classList.contains(className)) {
        element.classList.add(className);
    }
}

function setDataTableError(workspace, message = '') {
    let feedback = workspace.querySelector('[data-us-datatable-error]');
    if (!(feedback instanceof HTMLElement)) {
        feedback = document.createElement('div');
        feedback.className = 'alert alert-danger mb-3';
        feedback.dataset.usDatatableError = '';
        feedback.setAttribute('role', 'alert');
        feedback.setAttribute('aria-live', 'assertive');
        const surface = workspace.querySelector('.us-data-surface');
        surface?.parentElement
            ? surface.before(feedback)
            : workspace.prepend(feedback);
    }

    feedback.textContent = message;
    feedback.hidden = !message;
}

function dataTableApi(table) {
    try {
        if (window.jQuery?.fn?.DataTable?.isDataTable(table)) {
            return window.jQuery(table).DataTable();
        }
    } catch {
        // The native table remains available while DataTables is initializing.
    }
    return null;
}

function normalizedValue(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
        return value.map(item => normalizedValue(item?.name ?? item?.label ?? item)).filter(Boolean).join(', ');
    }
    if (value && typeof value === 'object') {
        return normalizedValue(value.name ?? value.label ?? value.displayName ?? value.number);
    }
    return '';
}

function valueAt(record, path) {
    return String(path || '')
        .split('.')
        .filter(Boolean)
        .reduce((value, segment) => value?.[segment], record);
}

function boxedColumns(table) {
    const columns = [];
    const api = dataTableApi(table);
    if (api?.columns) {
        api.columns().every(function (index) {
            const header = this.header();
            if (!header?.classList.contains('row-selector') &&
                !header?.classList.contains('responsive-control') &&
                !header?.classList.contains('empty') &&
                this.visible()) {
                columns.push({
                    index: Number.isInteger(index) ? index : header.cellIndex,
                    label: header.querySelector('.dt-column-title')?.textContent?.trim()
                        || header.textContent.trim(),
                    path: header.dataset.data || '',
                });
            }
        });
        return columns;
    }

    Array.from(table.tHead?.rows[0]?.cells ?? []).forEach((header, index) => {
        if (!header.classList.contains('row-selector') &&
            !header.classList.contains('responsive-control') &&
            !header.classList.contains('empty') &&
            !header.classList.contains('no-visible')) {
            columns.push({ index, label: header.textContent.trim(), path: header.dataset.data || '' });
        }
    });
    return columns;
}

function boxedRecords(table) {
    const api = dataTableApi(table);
    if (api?.rows) {
        const records = [];
        api.rows({ page: 'current', search: 'applied' }).every(function () {
            records.push({ data: this.data(), node: this.node() });
        });
        return records;
    }

    return Array.from(table.tBodies[0]?.rows ?? [])
        .filter(row => !row.classList.contains('child') &&
            !row.querySelector('td.dataTables_empty, td.dt-empty'))
        .map(row => ({ data: {}, node: row }));
}

function boxedCellValue(record, row, column) {
    const rendered = row?.cells[column.index]?.textContent?.trim();
    return rendered || normalizedValue(valueAt(record, column.path));
}

function appendBoxedValue(container, value, row, column) {
    const sourceLink = row?.cells[column.index]?.querySelector('a[href]');
    if (sourceLink) {
        const link = document.createElement('a');
        link.className = 'us-boxed-data-card__link';
        link.href = sourceLink.getAttribute('href');
        link.textContent = value || '—';
        container.append(link);
        return;
    }
    container.textContent = value || '—';
}

function createBoxedCard(record, columns) {
    const card = document.createElement('article');
    card.className = 'us-boxed-data-card';
    const row = record.node;
    const rowCheckbox = row?.querySelector('.row-selector input[type="checkbox"], .form-check input[type="checkbox"]');

    const header = document.createElement('header');
    header.className = 'us-boxed-data-card__header';
    const primary = columns[0];
    const primaryValue = primary ? boxedCellValue(record.data, row, primary) : '';
    const heading = document.createElement('div');
    heading.className = 'us-boxed-data-card__heading';
    if (primary) {
        const eyebrow = document.createElement('span');
        eyebrow.className = 'us-boxed-data-card__eyebrow';
        eyebrow.textContent = primary.label;
        const title = document.createElement('h3');
        title.className = 'us-boxed-data-card__title';
        appendBoxedValue(title, primaryValue, row, primary);
        heading.append(eyebrow, title);
    }
    header.append(heading);

    if (rowCheckbox) {
        const selection = document.createElement('label');
        selection.className = 'us-boxed-data-card__selection';
        const input = document.createElement('input');
        input.className = 'form-check-input';
        input.type = 'checkbox';
        input.checked = rowCheckbox.checked;
        input.setAttribute('aria-label', `${window.UnionStackUi?.text?.select || 'Select'} ${primaryValue}`.trim());
        card.classList.toggle('is-selected', input.checked || row?.classList.contains('selected'));
        input.addEventListener('change', () => {
            if (rowCheckbox.checked !== input.checked) {
                rowCheckbox.click();
            }
            card.classList.toggle('is-selected', input.checked);
        });
        selection.append(input);
        header.append(selection);
    }

    const details = document.createElement('dl');
    details.className = 'us-boxed-data-card__details';
    columns.slice(1).forEach(column => {
        const detail = document.createElement('div');
        detail.className = 'us-boxed-data-card__detail';
        const label = document.createElement('dt');
        label.textContent = column.label;
        const value = document.createElement('dd');
        appendBoxedValue(value, boxedCellValue(record.data, row, column), row, column);
        detail.append(label, value);
        details.append(detail);
    });

    card.append(header);
    if (details.childElementCount) {
        card.append(details);
    }
    return card;
}

function positionBoxedCardView(workspace, cardView) {
    const surface = workspace.querySelector('.us-data-surface');
    if (!surface) {
        return;
    }
    const tableLayout = surface.querySelector('.dt-layout-table');
    const table = surface.querySelector('table.data-table');
    const anchor = tableLayout || table;
    if (anchor && cardView.nextElementSibling !== anchor) {
        anchor.before(cardView);
    }
}

function boxedViewStorageKey(table) {
    return `unionstack:boxed-view:${table.dataset.tableid || table.id}`;
}

function readBoxedView(table) {
    try {
        return window.localStorage.getItem(boxedViewStorageKey(table)) === BOXED_CARD_VIEW
            ? BOXED_CARD_VIEW
            : BOXED_ROW_VIEW;
    } catch {
        return BOXED_ROW_VIEW;
    }
}

function storeBoxedView(table, view) {
    try {
        window.localStorage.setItem(boxedViewStorageKey(table), view);
    } catch {
        // Storage restrictions should not prevent switching views.
    }
}

function createBoxedViewButton(view, label) {
    const button = document.createElement('button');
    button.className = 'us-boxed-view-toggle__button';
    button.type = 'button';
    button.dataset.usBoxedView = view;
    button.title = label;
    button.setAttribute('aria-label', label);
    const glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    glyph.setAttribute('viewBox', '0 0 16 16');
    glyph.setAttribute('focusable', 'false');
    glyph.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute('d', view === BOXED_ROW_VIEW
        ? 'M2 2.75h12v1.5H2zm0 4.5h12v1.5H2zm0 4.5h12v1.5H2z'
        : 'M2 2h5v5H2zm7 0h5v5H9zM2 9h5v5H2zm7 0h5v5H9z');
    glyph.append(path);
    button.append(glyph);
    return button;
}

function enhanceBoxedView(workspace, table) {
    if (!workspace.classList.contains('data-table-boxed') || boxedViewControllers.has(workspace)
        || workspace.closest('[data-us-people-directory],[data-us-organization-directory],[data-us-case-directory]')) {
        return;
    }
    const actions = workspace.querySelector('.us-data-actions');
    const surface = workspace.querySelector('.us-data-surface');
    if (!actions || !surface) {
        return;
    }

    if (!table.id) {
        boxedViewId += 1;
        table.id = `us-boxed-datatable-${boxedViewId}`;
    }
    const labels = window.UnionStackUi?.text || {};
    const toggle = document.createElement('div');
    toggle.className = 'us-boxed-view-toggle';
    toggle.dataset.usBoxedViewToggle = '';
    toggle.setAttribute('role', 'group');
    toggle.setAttribute('aria-label', labels.viewAs || globalThis.UnionStackI18n.text("Common.ViewAs"));
    const rowsButton = createBoxedViewButton(BOXED_ROW_VIEW, labels.rows || 'Rows');
    const cardsButton = createBoxedViewButton(BOXED_CARD_VIEW, labels.cards || 'Cards');
    toggle.append(rowsButton, cardsButton);
    const lengthControl = actions.querySelector('.datatable-length');
    const insertionAnchor = lengthControl?.parentElement === actions ? lengthControl : null;
    actions.insertBefore(toggle, insertionAnchor);

    const cardView = document.createElement('section');
    cardView.id = `${table.id}-card-view`;
    cardView.className = 'us-boxed-card-view';
    cardView.dataset.usBoxedCardView = '';
    cardView.dataset.usDatatableObserverIgnore = '';
    cardView.setAttribute('aria-label', `${workspace.dataset.usTableLabel || table.getAttribute('aria-label') || ''} ${labels.cards || 'Cards'}`.trim());
    const grid = document.createElement('div');
    grid.className = 'us-boxed-card-grid';
    grid.dataset.usBoxedCardGrid = '';
    const empty = document.createElement('div');
    empty.className = 'us-boxed-card-empty';
    empty.dataset.usBoxedCardEmpty = '';
    empty.hidden = true;
    const emptyIcon = document.createElement('i');
    emptyIcon.className = 'bi bi-inbox';
    emptyIcon.setAttribute('aria-hidden', 'true');
    const emptyText = document.createElement('p');
    emptyText.textContent = labels.noResults || globalThis.UnionStackI18n.text("Common.ClientNoResultsa43619");
    empty.append(emptyIcon, emptyText);
    cardView.append(grid, empty);
    positionBoxedCardView(workspace, cardView);

    let preferredView = readBoxedView(table);
    const mobileViewQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia(BOXED_MOBILE_CARD_VIEW_QUERY)
        : null;

    const render = () => {
        positionBoxedCardView(workspace, cardView);
        const records = boxedRecords(table);
        const columns = boxedColumns(table);
        grid.replaceChildren(...records.map(record => createBoxedCard(record, columns)));
        grid.hidden = records.length === 0;
        empty.hidden = records.length > 0 || !table.tBodies.length;
    };
    let frame = 0;
    const scheduleRender = () => {
        if (workspace.dataset.usBoxedView !== BOXED_CARD_VIEW) {
            return;
        }
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(render);
    };
    const setView = (view, persist = true) => {
        const nextView = view === BOXED_CARD_VIEW ? BOXED_CARD_VIEW : BOXED_ROW_VIEW;
        workspace.dataset.usBoxedView = nextView;
        cardView.hidden = nextView !== BOXED_CARD_VIEW;
        if (nextView === BOXED_CARD_VIEW) {
            table.setAttribute('aria-hidden', 'true');
        } else {
            table.removeAttribute('aria-hidden');
        }
        toggle.querySelectorAll('[data-us-boxed-view]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.usBoxedView === nextView));
        });
        if (nextView === BOXED_CARD_VIEW) {
            render();
        } else {
            const api = dataTableApi(table);
            window.requestAnimationFrame(() => {
                api?.columns?.adjust?.();
                api?.responsive?.recalc?.();
            });
        }
        if (persist) {
            preferredView = nextView;
            storeBoxedView(table, nextView);
        }
    };

    toggle.querySelectorAll('[data-us-boxed-view]').forEach(button => {
        button.setAttribute('aria-controls', `${table.id} ${cardView.id}`);
        button.addEventListener('click', () => setView(button.dataset.usBoxedView));
    });
    workspace.addEventListener('unionstack:datatable:updated', scheduleRender);
    workspace.addEventListener('change', scheduleRender);
    window.jQuery?.(table)?.on?.(
        'draw.dt.unionstackBoxedCards column-visibility.dt.unionstackBoxedCards column-reorder.dt.unionstackBoxedCards',
        scheduleRender,
    );
    boxedViewControllers.set(workspace, { render, scheduleRender, setView });
    render();
    const applyResponsiveView = () => setView(
        mobileViewQuery?.matches ? BOXED_CARD_VIEW : preferredView,
        false,
    );
    if (typeof mobileViewQuery?.addEventListener === 'function') {
        mobileViewQuery.addEventListener('change', applyResponsiveView);
    } else {
        mobileViewQuery?.addListener?.(applyResponsiveView);
    }
    applyResponsiveView();
}

function refreshBoxedView(workspace) {
    boxedViewControllers.get(workspace)?.scheduleRender();
}

function resultCount(table) {
    try {
        if (window.jQuery?.fn?.DataTable?.isDataTable(table)) {
            return window.jQuery(table).DataTable().rows({ search: 'applied' }).count();
        }
    } catch {
        // Fall back to the rendered rows while DataTables is initializing.
    }

    return Array.from(table.tBodies[0]?.rows ?? [])
        .filter(row => !row.querySelector('td.dataTables_empty, td.dt-empty'))
        .length;
}

function updateTableState(workspace, table) {
    const empty = table.querySelector('tbody td.dataTables_empty, tbody td.dt-empty') !== null;
    const count = empty ? 0 : resultCount(table);
    const status = workspace.querySelector('[data-us-datatable-status]');
    const message = globalThis.UnionStackI18n.text('Common.IntegrationResultsUpdatedCount', count);

    workspace.dataset.usEmpty = String(empty);
    if (status && status.textContent !== message) {
        status.textContent = message;
    }

    table.dispatchEvent(new CustomEvent('unionstack:datatable:updated', {
        bubbles: true,
        detail: { count, empty },
    }));
}

function labelControls(workspace) {
    for (const control of workspace.querySelectorAll('button, [role="button"]')) {
        if (!control.getAttribute('aria-label')) {
            const label = control.getAttribute('title')
                || control.querySelector('[title]')?.getAttribute('title')
                || control.textContent.trim();
            if (label) {
                control.setAttribute('aria-label', label);
            }
        }

        if (control.classList.contains('disabled')) {
            control.setAttribute('aria-disabled', 'true');
        } else {
            control.removeAttribute('aria-disabled');
        }
    }
}

function prepareWorkspace(workspace, table) {
    addClass(workspace, 'us-data-workspace');

    const toolbar = workspace.querySelector('.row.justify-content-between');
    addClass(toolbar, 'us-data-toolbar');
    const actionColumn = toolbar?.querySelector('.us-data-actions')
        || toolbar?.querySelector('.col-12.col-md-auto, .col-12.col-sm-auto');
    if (actionColumn) {
        addClass(actionColumn, 'us-data-actions');
        actionColumn.setAttribute('role', 'toolbar');
        actionColumn.setAttribute('aria-label', workspace.dataset.usActionsLabel
            || window.UnionStackUi?.text?.actions
            || globalThis.UnionStackI18n.text("Common.Actions"));
    }

    addClass(workspace.querySelector('.search-input-container'), 'us-data-search');
    const search = workspace.querySelector('.datatable-search');
    if (search) {
        search.type = 'search';
        search.setAttribute('aria-label', search.getAttribute('aria-label')
            || `${window.UnionStackUi?.text?.search || 'Search'} ${workspace.dataset.usTableLabel || ''}`.trim());
    }

    let status = workspace.querySelector('[data-us-datatable-status]');
    if (!status) {
        status = document.createElement('div');
        status.className = 'us-visually-hidden-live';
        status.dataset.usDatatableStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
        workspace.append(status);
    }

    const label = workspace.dataset.usTableLabel
        || table.dataset.exportName
        || workspace.closest('section')?.querySelector('h2')?.textContent?.trim()
        || table.id;
    workspace.dataset.usTableLabel = label;
    table.setAttribute('aria-label', table.getAttribute('aria-label') || label);

    let surface = table.closest('.data-table-responsive-wrapper, .dt-container');
    if (!surface && workspace.classList.contains('data-table-boxed')) {
        surface = document.createElement('div');
        surface.className = 'data-table-responsive-wrapper';
        table.before(surface);
        surface.append(table);
    }
    if (surface) {
        addClass(surface, 'us-data-surface');
        surface.setAttribute('role', 'region');
        surface.setAttribute('aria-label', label);
        surface.tabIndex = 0;
    }
}

function makeCheckAllKeyboardAccessible(workspace) {
    const control = workspace.querySelector('.datatable-check-all-button');
    if (!control || control.dataset.usKeyboardEnhanced === 'true') {
        return;
    }

    control.dataset.usKeyboardEnhanced = 'true';
    control.setAttribute('role', 'button');
    control.setAttribute('tabindex', '0');
    const checkbox = control.querySelector('input[type="checkbox"]');
    if (checkbox?.id) {
        control.setAttribute('aria-controls', workspace.querySelector('table')?.id || '');
    }

    control.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        event.preventDefault();
        control.click();
    });
}

function syncResponsiveDetails(workspace, table) {
    const expandLabel = workspace.dataset.usExpandRowLabel || 'Show row details';
    const collapseLabel = workspace.dataset.usCollapseRowLabel || 'Hide row details';
    const tableId = table.id || 'datatable';
    const responsiveAvailable = table.classList.contains('collapsed');
    const headerControl = table.querySelector('thead th.responsive-control');
    headerControl?.setAttribute('aria-hidden', String(!responsiveAvailable));
    const rows = Array.from(table.tBodies[0]?.rows ?? []).filter(row => !row.classList.contains('child'));

    rows.forEach((row, index) => {
        const control = row.querySelector('.dtr-control');
        if (!control) {
            return;
        }

        const expanded = row.classList.contains('dtr-expanded');
        const dedicatedControl = control.classList.contains('us-responsive-control') || Boolean(headerControl);
        const controlId = `${tableId}-responsive-toggle-${index}`;
        const detailsId = `${tableId}-responsive-details-${index}`;
        control.id = controlId;
        control.setAttribute('aria-hidden', String(!responsiveAvailable && dedicatedControl));
        if (!responsiveAvailable) {
            control.removeAttribute('role');
            control.setAttribute('tabindex', '-1');
            control.removeAttribute('aria-expanded');
            control.removeAttribute('aria-controls');
            control.removeAttribute('aria-label');
            return;
        }

        control.setAttribute('role', 'button');
        control.setAttribute('tabindex', '0');
        control.setAttribute('aria-expanded', String(expanded));
        control.setAttribute('aria-controls', detailsId);
        const actionLabel = expanded ? collapseLabel : expandLabel;
        control.setAttribute('aria-label', dedicatedControl ? actionLabel : `${actionLabel}: ${control.textContent.trim()}`);

        const details = row.nextElementSibling?.classList.contains('child')
            ? row.nextElementSibling
            : null;
        if (details) {
            details.id = detailsId;
            details.dataset.usResponsiveDetails = '';
            details.setAttribute('aria-labelledby', controlId);

            const detailsCell = details.cells[0];
            const headerRows = table.tHead?.rows;
            const columnCount = headerRows?.[headerRows.length - 1]?.cells.length || row.cells.length;
            if (detailsCell && detailsCell.colSpan !== columnCount) {
                detailsCell.colSpan = columnCount;
            }
        }

        if (control.dataset.usResponsiveKeyboardEnhanced !== 'true') {
            control.dataset.usResponsiveKeyboardEnhanced = 'true';
            control.addEventListener('keydown', event => {
                if (event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }
                event.preventDefault();
                control.click();
            });
        }
    });
}

function syncLengthControl(workspace, table) {
    const control = workspace.querySelector('.datatable-length');
    if (!control) {
        return;
    }

    let length;
    try {
        if (window.jQuery?.fn?.DataTable?.isDataTable(table)) {
            length = Number(window.jQuery(table).DataTable().page.len());
        }
    } catch {
        return;
    }
    if (!Number.isFinite(length)) {
        return;
    }

    const choices = Array.from(control.querySelectorAll('.dropdown-item'));
    const selected = choices.find(choice => Number.parseInt(choice.textContent, 10) === length);
    choices.forEach(choice => choice.classList.toggle('active', choice === selected));

    const label = control.querySelector('button > span');
    if (label) {
        label.textContent = selected?.textContent?.trim() || String(length);
        label.parentElement.setAttribute('aria-label', label.textContent);
    }
}

function attachDataTableEvents(workspace, table) {
    if (!window.jQuery) {
        return;
    }

    const dataTable = window.jQuery(table);
    dataTable.on('processing.dt.unionstackUi', (_event, _settings, processing) => {
        setBusy(workspace, processing, processing ? workspace.dataset.usLoadingLabel : undefined);
    });
    dataTable.on('draw.dt.unionstackUi', () => {
        setDataTableError(workspace);
        syncLengthControl(workspace, table);
        syncResponsiveDetails(workspace, table);
        updateTableState(workspace, table);
        refreshBoxedView(workspace);
    });
    dataTable.on('length.dt.unionstackUi stateLoaded.dt.unionstackUi', () => {
        syncLengthControl(workspace, table);
    });
    dataTable.on('responsive-display.dt.unionstackUi', () => {
        window.requestAnimationFrame(() => syncResponsiveDetails(workspace, table));
    });
    dataTable.on('dt-error.dt.unionstackUi', () => {
        setBusy(workspace, false);
        DataTableSupport.reportLoadError(table);
    });
}

function observeResponsiveWidth(workspace, table) {
    if (workspace.dataset.usResponsiveWidthObserved === 'true' || typeof ResizeObserver === 'undefined') {
        return;
    }

    workspace.dataset.usResponsiveWidthObserved = 'true';
    let previousWidth = -1;
    let frame = 0;
    const observer = new ResizeObserver(entries => {
        const width = Math.round(entries[0]?.contentRect?.width ?? workspace.getBoundingClientRect().width);
        if (width <= 0 || width === previousWidth) {
            return;
        }

        previousWidth = width;
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
            try {
                if (window.jQuery?.fn?.DataTable?.isDataTable(table)) {
                    const api = window.jQuery(table).DataTable();
                    api.columns?.adjust?.();
                    api.responsive?.recalc?.();
                }
            } catch {
                // The table remains usable while DataTables is still initializing.
            }
            syncResponsiveDetails(workspace, table);
        });
    });
    observer.observe(workspace);
}

function isIgnoredObserverMutation(mutation) {
    return mutation.target instanceof Element
        && mutation.target.closest('[data-us-datatable-observer-ignore]') !== null;
}

function enhanceDataTable(workspace) {
    if (workspace.dataset.usDatatableEnhanced === 'true') {
        return;
    }

    const table = workspace.querySelector('table.data-table');
    if (!table) {
        return;
    }

    workspace.dataset.usDatatableEnhanced = 'true';
    prepareWorkspace(workspace, table);
    enhanceBoxedView(workspace, table);
    labelControls(workspace);
    makeCheckAllKeyboardAccessible(workspace);
    attachDataTableEvents(workspace, table);
    observeResponsiveWidth(workspace, table);
    syncLengthControl(workspace, table);
    syncResponsiveDetails(workspace, table);

    const observer = new MutationObserver(mutations => {
        if (mutations.length > 0 && mutations.every(isIgnoredObserverMutation)) {
            return;
        }

        prepareWorkspace(workspace, table);
        enhanceBoxedView(workspace, table);
        labelControls(workspace);
        syncResponsiveDetails(workspace, table);
        updateTableState(workspace, table);
        refreshBoxedView(workspace);
    });
    observer.observe(workspace, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
    });

    updateTableState(workspace, table);
}

function enhanceDataTables(root = document) {
    for (const workspace of root.querySelectorAll('[data-us-datatable-workspace], .data-table-boxed')) {
        enhanceDataTable(workspace);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhanceDataTables(), { once: true });
} else {
    enhanceDataTables();
}

export {
    boxedColumns,
    boxedRecords,
    createBoxedCard,
    enhanceBoxedView,
    enhanceDataTable,
    enhanceDataTables,
    isIgnoredObserverMutation,
    observeResponsiveWidth,
    resultCount,
    syncLengthControl,
    syncResponsiveDetails,
    updateTableState,
};
